// client/src/components/pages/PeriodSummary/Sparkline.js
//
// The table's "Activity" cell (ST-00174 UX pass): a ~120×24 inline SVG of the
// group's daily tonnes across the whole period, mirrored the same way as the
// Timeline swimlanes (dispatch up, receipt down) so the two views teach the
// same shape.
//
// Node budget matters here — the table renders up to 2 000 rows. Each sparkline
// is therefore FOUR shared elements (weekend wash, baseline, one path for the
// dispatch bars, one for the receipt bars) plus one transparent <rect> per
// ACTIVE day to carry the hover title. A group active on 20 days costs ~24
// nodes, not 120.

import React from 'react';
import { DISPATCH_COLOR, RECEIPT_COLOR, formatTonnes, formatCount, isWeekend } from './utils';

const WIDTH = 92;
const HEIGHT = 24;
const PAD = 2; // px of headroom kept above/below so a max bar never touches the edge

/** `x y w h` → the `M…h…v…h…Z` of one rect, appended to a shared path. */
const rect = (x, y, w, h) => `M${x} ${y}h${w}v${h}h${-w}Z`;

const Sparkline = ({ days = [], entry, side = 'both', keyLabel }) => {
  if (!entry || !days.length || !(entry.days || []).length) {
    return (
      <span className="text-gray-300" title="No daily activity loaded for this key">
        —
      </span>
    );
  }

  const colW = WIDTH / days.length;
  const barW = Math.max(1, Math.min(colW - 0.6, 6));
  const max = entry.max || 1;

  // Calendar date → column index, so the sparse day rows can be placed without
  // scanning the calendar per day.
  const indexOf = new Map(days.map((d, i) => [d, i]));

  const mirrored = side === 'both';
  const baseline = mirrored ? HEIGHT / 2 : HEIGHT - PAD;
  const span = mirrored ? HEIGHT / 2 - PAD : HEIGHT - PAD * 2;

  const height = (tonnes) => {
    const v = Number(tonnes) || 0;
    if (v <= 0) return 0;
    return Math.max(1, Math.min(span, (v / max) * span));
  };

  let weekendPath = '';
  days.forEach((date, i) => {
    if (isWeekend(date)) weekendPath += rect(i * colW, 0, colW, HEIGHT);
  });

  let upPath = '';
  let downPath = '';
  const hits = [];

  (entry.days || []).forEach((d) => {
    const i = indexOf.get(d.date);
    if (i === undefined) return;
    const x = i * colW + (colW - barW) / 2;

    if (mirrored) {
      const up = height(d.dispatchTonnes);
      const down = height(d.receiptTonnes);
      if (up > 0) upPath += rect(x, baseline - up, barW, up);
      if (down > 0) downPath += rect(x, baseline, barW, down);
    } else {
      const h = height(side === 'dispatch' ? d.dispatchTonnes : d.receiptTonnes);
      if (h > 0) {
        const path = rect(x, baseline - h, barW, h);
        if (side === 'dispatch') upPath += path;
        else downPath += path;
      }
    }

    hits.push({
      date: d.date,
      x: i * colW,
      title:
        `${d.date} · ${formatCount(d.tickets)} tickets\n` +
        `dispatch ${formatTonnes(d.dispatchTonnes)} t · receipt ${formatTonnes(d.receiptTonnes)} t`,
    });
  });

  return (
    <svg
      width={WIDTH}
      height={HEIGHT}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      className="text-gray-200 overflow-visible"
      role="img"
      aria-label={`Daily activity for ${keyLabel || 'this key'}: ${hits.length} active days, peak ${formatTonnes(max)} t`}
      data-testid="activity-sparkline"
    >
      {weekendPath && <path d={weekendPath} fill="currentColor" opacity="0.35" />}
      <rect x="0" y={baseline - 0.5} width={WIDTH} height="1" fill="currentColor" />
      {upPath && <path d={upPath} fill={DISPATCH_COLOR} />}
      {downPath && <path d={downPath} fill={RECEIPT_COLOR} />}
      {hits.map((h) => (
        <rect key={h.date} x={h.x} y="0" width={colW} height={HEIGHT} fill="transparent">
          <title>{h.title}</title>
        </rect>
      ))}
    </svg>
  );
};

// The table re-renders on every sort / search keystroke; the sparklines only
// change when the period does.
export default React.memo(Sparkline);
